import { useAuth } from '../context/AuthContext';

/**
 * Hook to check user permissions
 * @returns {Object} Permission checking utilities
 */
export const usePermissions = () => {
  const { user } = useAuth();
  
  // Super admin has all permissions
  const isSuperAdmin = user?.role === 'super_admin';
  
  // Get permissions array from user
  const permissions = user?.permissions || [];
  
  /**
   * Check if user has permission for a specific resource and action
   * @param {string} resource - Resource type (products, categories, etc.)
   * @param {string} action - Action type (create, read, update, delete)
   * @returns {boolean}
   */
  const hasPermission = (resource, action = 'read') => {
    // Super admin has all permissions
    if (isSuperAdmin) return true;
    
    // Admin role has all permissions
    if (user?.role === 'admin') return true;
    
    // Find permission for this resource
    const permission = permissions.find(p => p.permission_type === resource);
    if (!permission) return false;
    
    // Check the specific action
    switch (action) {
      case 'create':
        return permission.can_create;
      case 'read':
        return permission.can_read;
      case 'update':
        return permission.can_update;
      case 'delete':
        return permission.can_delete;
      default:
        return permission.can_read;
    }
  };
  
  /**
   * Check if user can access a specific admin section
   * @param {string} section - Section name (products, categories, etc.)
   * @returns {boolean}
   */
  const canAccessSection = (section) => {
    // Super admin and admin can access everything
    if (isSuperAdmin || user?.role === 'admin') return true;
    
    // Map section names to permission types
    const sectionMap = {
      'dashboard': null, // Everyone can access dashboard
      'products': 'products',
      'categories': 'categories',
      'orders': 'orders',
      'reviews': 'reviews',
      'banners': 'banners',
      'settings': 'settings',
      'messages': 'messages',
      'users': null, // Only super_admin
    };
    
    // Users section is super_admin only
    if (section === 'users') return isSuperAdmin;
    
    // Dashboard is accessible to all admin roles
    if (section === 'dashboard') return true;
    
    const resource = sectionMap[section];
    if (!resource) return false;
    
    return hasPermission(resource, 'read');
  };
  
  /**
   * Get all accessible sections for current user
   * @returns {string[]}
   */
  const getAccessibleSections = () => {
    const allSections = ['dashboard', 'products', 'categories', 'orders', 'reviews', 'banners', 'settings', 'messages', 'users'];
    return allSections.filter(section => canAccessSection(section));
  };
  
  return {
    permissions,
    isSuperAdmin,
    hasPermission,
    canAccessSection,
    getAccessibleSections,
    role: user?.role
  };
};

export default usePermissions;
